/**
 * Matrix Traffic Analyzer — DEBUG builds only.
 *
 * Intercepts all HTTP traffic (via fetchFn wrapper) and SDK events to provide
 * a statistical view of the client↔server communication. Zero-overhead in
 * production because webpack dead-code elimination removes this module when
 * process.env.DEBUG !== 'true'.
 */
import { ClientEvent } from 'matrix-js-sdk';
import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api';
// ─── Constants ────────────────────────────────────────────────────────────────
const BUFFER_SIZE = 200;
// Maps URL path patterns to human-readable category names.
const ENDPOINT_CATEGORIES = [
    [/\/keys\/upload/, 'keys/upload (OTK replenishment)'],
    [/\/keys\/query/, 'keys/query (device lookup)'],
    [/\/keys\/claim/, 'keys/claim (Olm session)'],
    [/\/keys\/changes/, 'keys/changes'],
    [/\/sendToDevice/, 'sendToDevice (key distribution)'],
    [/\/send\/m\.room\.encrypted/, 'send (encrypted message)'],
    [/\/send\//, 'send (plaintext message)'],
    [/\/sync/, 'sync'],
    [/\/dehydrated_device/, 'dehydrated_device'],
    [/\/login/, 'login'],
    [/\/logout/, 'logout'],
    [/\/upload/, 'media/upload'],
    [/\/download/, 'media/download'],
    [/\/join\//, 'room/join'],
    [/\/leave/, 'room/leave'],
    [/\/createRoom/, 'room/create'],
    [/\/profile/, 'profile'],
    [/\/presence/, 'presence'],
    [/\/voip\//, 'voip'],
    [/\/_matrix\/push/, 'push'],
];
function categorizeUrl(url) {
    try {
        const pathname = new URL(url).pathname;
        for (const [pattern, label] of ENDPOINT_CATEGORIES) {
            if (pattern.test(pathname))
                return label;
        }
        // Strip matrix path prefix for the fallback label
        const match = pathname.match(/\/_matrix\/(?:client|media)\/[^/]+(\/.*)$/);
        return match ? match[1] : pathname;
    }
    catch {
        return url.slice(0, 60);
    }
}
// ─── Circular buffer ──────────────────────────────────────────────────────────
class CircularBuffer {
    capacity;
    buf = [];
    pos = 0;
    full = false;
    constructor(capacity) {
        this.capacity = capacity;
    }
    push(item) {
        if (this.full) {
            this.buf[this.pos] = item;
        }
        else {
            this.buf.push(item);
            if (this.buf.length === this.capacity)
                this.full = true;
        }
        this.pos = (this.pos + 1) % this.capacity;
    }
    /** Returns items from newest to oldest. */
    toArray() {
        if (!this.full)
            return [...this.buf].reverse();
        const head = this.buf.slice(this.pos).reverse();
        const tail = this.buf.slice(0, this.pos).reverse();
        return [...head, ...tail];
    }
    clear() {
        this.buf = [];
        this.pos = 0;
        this.full = false;
    }
}
// ─── Analyzer singleton ───────────────────────────────────────────────────────
class MatrixTrafficAnalyzer {
    requestStats = new Map();
    eventStats = new Map();
    recentRequests = new CircularBuffer(BUFFER_SIZE);
    recentEvents = new CircularBuffer(BUFFER_SIZE);
    startedAt = Date.now();
    totalRequests = 0;
    totalErrors = 0;
    totalEvents = 0;
    attachedClient = null;
    // ── HTTP interception ────────────────────────────────────────────────────
    /**
     * Returns a wrapped fetch function suitable for passing as `fetchFn` to
     * `sdk.createClient()`. Records timing, category, and status for every call.
     */
    createFetchInterceptor(originalFetch) {
        return async (resource, init) => {
            const url = typeof resource === 'string'
                ? resource
                : resource instanceof URL
                    ? resource.toString()
                    : resource.url;
            const method = init?.method ?? (resource instanceof Request ? resource.method : 'GET');
            const category = categorizeUrl(url);
            const start = performance.now();
            this.totalRequests++;
            try {
                const response = await originalFetch(resource, init);
                const durationMs = Math.round(performance.now() - start);
                this.recordRequest(category, durationMs, response.status, method, url, response.ok ? undefined : `HTTP ${response.status}`);
                return response;
            }
            catch (err) {
                const durationMs = Math.round(performance.now() - start);
                const message = err instanceof Error ? err.message : String(err);
                this.recordRequest(category, durationMs, null, method, url, message);
                throw err;
            }
        };
    }
    recordRequest(category, durationMs, status, method, url, error) {
        const isError = !!error;
        if (isError)
            this.totalErrors++;
        // Aggregate stats
        let stat = this.requestStats.get(category);
        if (!stat) {
            stat = { category, count: 0, errors: 0, totalMs: 0, lastTimestamp: 0 };
            this.requestStats.set(category, stat);
        }
        stat.count++;
        stat.totalMs += durationMs;
        stat.lastTimestamp = Date.now();
        if (isError)
            stat.errors++;
        // Recent log
        this.recentRequests.push({
            timestamp: Date.now(),
            method: method.toUpperCase(),
            category,
            url,
            status,
            durationMs,
            error,
        });
    }
    // ── Event listeners ──────────────────────────────────────────────────────
    attachEventListeners(client) {
        if (this.attachedClient === client)
            return;
        if (this.attachedClient)
            this.detachEventListeners();
        this.attachedClient = client;
        client.on(ClientEvent.Sync, (state) => {
            this.recordEvent('sync', `sync:${state}`, `SyncState → ${state}`);
        });
        client.on(ClientEvent.Event, (event) => {
            const t = event.getType();
            this.recordEvent('timeline', t, `room:${event.getRoomId() ?? '?'}`);
        });
        client.on(ClientEvent.ToDeviceEvent, (event) => {
            const t = event.getType();
            const detail = `from:${event.getSender() ?? '?'}`;
            this.recordEvent('to-device', t, detail);
        });
        client.on(ClientEvent.ReceivedToDeviceMessage, (payload) => {
            const t = payload.message.type ?? 'unknown';
            const encrypted = payload.encryptionInfo !== null;
            this.recordEvent('to-device', `${t}${encrypted ? ' (encrypted)' : ' (plain)'}`, `sender:${payload.message.sender}`);
        });
        // Crypto events
        const cryptoClient = client;
        cryptoClient.on(CryptoEvent.KeysChanged, () => {
            this.recordEvent('crypto', 'crypto:keysChanged', '');
        });
        cryptoClient.on(CryptoEvent.DevicesUpdated, (users) => {
            this.recordEvent('crypto', 'crypto:devicesUpdated', `users:${users.length}`);
        });
        cryptoClient.on(CryptoEvent.KeyBackupStatus, (enabled) => {
            this.recordEvent('crypto', 'crypto:keyBackupStatus', enabled ? 'enabled' : 'disabled');
        });
        cryptoClient.on(CryptoEvent.KeyBackupFailed, (errCode) => {
            this.recordEvent('crypto', 'crypto:keyBackupFailed', errCode);
        });
        cryptoClient.on(CryptoEvent.KeyBackupSessionsRemaining, (remaining) => {
            this.recordEvent('crypto', 'crypto:keyBackupSessionsRemaining', `remaining:${remaining}`);
        });
        cryptoClient.on(CryptoEvent.UserTrustStatusChanged, (userId) => {
            this.recordEvent('crypto', 'crypto:userTrustChanged', `user:${userId}`);
        });
    }
    detachEventListeners() {
        // matrix-js-sdk does not expose an easy "remove all" for a specific
        // subscriber, so we simply null the reference and stop recording.
        // Leftover listeners are harmless once the client is replaced.
        this.attachedClient = null;
    }
    recordEvent(category, type, detail) {
        this.totalEvents++;
        let stat = this.eventStats.get(type);
        if (!stat) {
            stat = { type, count: 0, lastTimestamp: 0 };
            this.eventStats.set(type, stat);
        }
        stat.count++;
        stat.lastTimestamp = Date.now();
        this.recentEvents.push({ timestamp: Date.now(), category, type, detail });
    }
    // ── Public API ───────────────────────────────────────────────────────────
    getSnapshot() {
        return {
            requestStats: Array.from(this.requestStats.values())
                .sort((a, b) => b.count - a.count),
            eventStats: Array.from(this.eventStats.values())
                .sort((a, b) => b.count - a.count),
            recentRequests: this.recentRequests.toArray(),
            recentEvents: this.recentEvents.toArray(),
            startedAt: this.startedAt,
            totalRequests: this.totalRequests,
            totalErrors: this.totalErrors,
            totalEvents: this.totalEvents,
        };
    }
    reset() {
        this.requestStats.clear();
        this.eventStats.clear();
        this.recentRequests.clear();
        this.recentEvents.clear();
        this.startedAt = Date.now();
        this.totalRequests = 0;
        this.totalErrors = 0;
        this.totalEvents = 0;
    }
    getRequestCount(category) {
        return this.requestStats.get(category)?.count ?? 0;
    }
    /**
     * Call before sending an encrypted room message; invoke the returned finish()
     * after send completes. Logs a console warning when no key-distribution traffic
     * occurred during the send window (likely missing m.room_key to-device delivery).
     */
    beginEncryptedSendWatch(roomId) {
        const sendToDeviceBefore = this.getRequestCount('sendToDevice (key distribution)');
        const keysClaimBefore = this.getRequestCount('keys/claim (Olm session)');
        const keysQueryBefore = this.getRequestCount('keys/query (device lookup)');
        const startedAt = Date.now();
        return () => {
            const sendToDeviceAfter = this.getRequestCount('sendToDevice (key distribution)');
            const keysClaimAfter = this.getRequestCount('keys/claim (Olm session)');
            const keysQueryAfter = this.getRequestCount('keys/query (device lookup)');
            const sendToDeviceDelta = sendToDeviceAfter - sendToDeviceBefore;
            const keysClaimDelta = keysClaimAfter - keysClaimBefore;
            const keysQueryDelta = keysQueryAfter - keysQueryBefore;
            if (sendToDeviceDelta === 0) {
                console.warn('[E2EE] Encrypted message sent without sendToDevice key distribution. ' +
                    'Recipients may see MEGOLM_UNKNOWN_INBOUND_SESSION_ID until SDK retry or key request.', {
                    roomId,
                    elapsedMs: Date.now() - startedAt,
                    sendToDeviceDelta,
                    keysClaimDelta,
                    keysQueryDelta,
                });
            }
        };
    }
}
// Export a singleton — but only when DEBUG is true.
// In production webpack replaces the `if` condition with `if (false)` and
// tree-shakes the entire module away.
let analyzer = null;
if (process.env.DEBUG === 'true') {
    analyzer = new MatrixTrafficAnalyzer();
}
export default analyzer;
